import {
  RecommendedSeatsRequest,
  ReleaseSeatHoldsRequest,
  SeatAssignment,
  SeatCabinType,
  SeatHoldRequest,
  SeatHoldResponse,
  SeatMapLayout,
  SeatMapRequest,
  SeatMapSeat,
  SeatMapSection,
} from "../types/service-types/SeatMapTypes";
import axios from "./axios.interceptors";

interface SeatMapApiSeat {
  id?: string;
  label?: string;
  columnLetter?: string;
  rowNumber?: number;
  seatClassName?: string;
  status?: string;
  price?: number;
  nearAisle?: boolean;
  nearWindow?: boolean;
  nearExit?: boolean;
  nearLavatory?: boolean;
  overWing?: boolean;
  allowChildSelected?: boolean;
  blocked?: boolean;
}

interface SeatMapApiRow {
  rowNumber: number;
  seats: SeatMapApiSeat[];
}

interface SeatMapApiResponse {
  flightId?: string;
  flightNumber?: string;
  aircraftCode?: string;
  aircraftName?: string;
  totalRows?: number;
  columns?: string[];
  currency?: string;
  rows?: SeatMapApiRow[];
}

const seatTypeRank: Record<SeatCabinType, number> = {
  standard: 0,
  front: 1,
  "extra-legroom": 2,
};

const getPassengerName = (passenger: any, index: number) => {
  const fullName = [passenger?.name, passenger?.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || `Passenger ${index + 1}`;
};

const isInfantPassenger = (passenger: any) => {
  const ageCategory = String(passenger?.ageCategory || "").toUpperCase();
  return ageCategory === "INF" || ageCategory === "INFANT";
};

const normalizeString = (value?: string) => (value || "").toLowerCase();

const normalizeSeatClassName = (value?: string) => {
  const className = (value || "").trim();
  return className || undefined;
};

const getSeatType = (
  seat: SeatMapApiSeat,
  minAvailablePrice?: number
): SeatCabinType => {
  const className = normalizeString(seat.seatClassName);

  if (
    seat.nearExit ||
    className.includes("exit") ||
    className.includes("extra") ||
    className.includes("legroom") ||
    className.includes("space") ||
    className.includes("business")
  ) {
    return "extra-legroom";
  }

  if (
    className.includes("front") ||
    (!seat.blocked &&
      seat.status?.toUpperCase() === "AVAILABLE" &&
      minAvailablePrice != null &&
      Number(seat.price) > minAvailablePrice)
  ) {
    return "front";
  }

  return "standard";
};

const getSectionCopy = (type: SeatCabinType, hasExitRow: boolean) => {
  if (type === "extra-legroom" && hasExitRow) {
    return {
      labelKey: "app.seatMap.sections.exit",
      fallbackLabel: "Emergency exit",
    };
  }

  if (type === "extra-legroom") {
    return {
      labelKey: "app.seatMap.sections.extraFront",
      fallbackLabel: "Extra legroom up front",
    };
  }

  if (type === "front") {
    return {
      labelKey: "app.seatMap.sections.front",
      fallbackLabel: "Get off quick",
    };
  }

  return {
    labelKey: "app.seatMap.sections.standard",
    fallbackLabel: "Standard",
  };
};

const getSeatStatus = (seat: SeatMapApiSeat): SeatMapSeat["status"] => {
  if (seat.blocked) return "reserved";
  return seat.status?.toUpperCase() === "AVAILABLE" ? "available" : "reserved";
};

const getRowType = (rowSeats: SeatMapSeat[]): SeatCabinType => {
  return rowSeats.reduce<SeatCabinType>((highest, seat) => {
    return seatTypeRank[seat.type] > seatTypeRank[highest] ? seat.type : highest;
  }, "standard");
};

const getRowSeatClassName = (rowSeats: SeatMapSeat[]) => {
  const classNames = Array.from(
    new Set(rowSeats.map((seat) => seat.seatClassName).filter(Boolean))
  );

  return classNames.length === 1 ? classNames[0] : undefined;
};

const getSectionMinPrice = (seats: SeatMapSeat[]) => {
  const prices = seats
    .filter((seat) => seat.status === "available")
    .map((seat) => Number(seat.price))
    .filter((price) => Number.isFinite(price));

  return prices.length ? Math.min(...prices) : undefined;
};

const buildSections = (
  rowNumbers: number[],
  rowSeats: Record<number, SeatMapSeat[]>,
  currencyCode: string
): SeatMapSection[] => {
  const sections: SeatMapSection[] = [];

  rowNumbers.forEach((rowNumber) => {
    const seats = rowSeats[rowNumber] || [];
    const type = getRowType(seats);
    const seatClassName = getRowSeatClassName(seats);
    const hasExitRow = seats.some((seat) => seat.nearExit);
    const previous = sections[sections.length - 1];
    const isContiguous =
      !!previous && previous.rows[previous.rows.length - 1] + 1 === rowNumber;
    const sectionLabel =
      seatClassName || getSectionCopy(type, hasExitRow).fallbackLabel;

    if (
      !previous ||
      (!seatClassName && previous.type !== type) ||
      previous.seatClassName !== seatClassName ||
      !isContiguous
    ) {
      sections.push({
        id: `${seatClassName || type}-${sections.length}`,
        labelKey: seatClassName
          ? `app.seatMap.sections.${seatClassName.toLowerCase()}`
          : getSectionCopy(type, hasExitRow).labelKey,
        fallbackLabel: sectionLabel,
        rows: [rowNumber],
        type,
        seatClassName,
        minPrice: getSectionMinPrice(seats),
        currencyCode,
      });
      return;
    }

    previous.rows.push(rowNumber);
    const nextMinPrice = getSectionMinPrice(seats);
    if (
      nextMinPrice != null &&
      (previous.minPrice == null || nextMinPrice < previous.minPrice)
    ) {
      previous.minPrice = nextMinPrice;
    }

    if (type === "extra-legroom" && hasExitRow) {
      previous.labelKey = "app.seatMap.sections.exit";
      previous.fallbackLabel = "Emergency exit";
    }

    if (seatClassName) {
      previous.labelKey = `app.seatMap.sections.${seatClassName.toLowerCase()}`;
      previous.fallbackLabel = seatClassName;
    }
  });

  return sections;
};

const normalizeSeatMap = (
  data: SeatMapApiResponse,
  fallbackCurrencyCode = "EUR"
): SeatMapLayout => {
  const apiRows = data.rows || [];
  const columns =
    data.columns?.length
      ? data.columns
      : Array.from(
          new Set(
            apiRows.flatMap((row) =>
              (row.seats || [])
                .map((seat) => seat.columnLetter)
                .filter((column): column is string => !!column)
            )
          )
        ).sort();

  const currencyCode = data.currency || fallbackCurrencyCode;
  const availablePrices = apiRows
    .flatMap((row) => row.seats || [])
    .filter(
      (seat) =>
        seat.status?.toUpperCase() === "AVAILABLE" &&
        !seat.blocked &&
        Number.isFinite(Number(seat.price))
    )
    .map((seat) => Number(seat.price));
  const minAvailablePrice = availablePrices.length
    ? Math.min(...availablePrices)
    : undefined;

  const seats: SeatMapSeat[] = apiRows.flatMap((row) => {
    const rowNumber = row.rowNumber;

    return (row.seats || []).map((seat) => {
      const column = seat.columnLetter || "";
      const label = seat.label || `${rowNumber}${column}`;

      return {
        id: label,
        flightSeatId: seat.id || undefined,
        label,
        row: rowNumber,
        column,
        type: getSeatType(seat, minAvailablePrice),
        seatClassName: normalizeSeatClassName(seat.seatClassName),
        status: getSeatStatus(seat),
        price: Number(seat.price) || 0,
        currencyCode,
        backendStatus: seat.status,
        nearAisle: !!seat.nearAisle,
        nearWindow: !!seat.nearWindow,
        nearExit: !!seat.nearExit,
        nearLavatory: !!seat.nearLavatory,
        overWing: !!seat.overWing,
        allowChildSelected: seat.allowChildSelected !== false,
        blocked: !!seat.blocked,
      };
    });
  });

  const columnRank = new Map(columns.map((column, index) => [column, index]));
  seats.sort(
    (a, b) =>
      a.row - b.row ||
      (columnRank.get(a.column) ?? Number.MAX_SAFE_INTEGER) -
        (columnRank.get(b.column) ?? Number.MAX_SAFE_INTEGER)
  );

  const rowSeats = seats.reduce<Record<number, SeatMapSeat[]>>((acc, seat) => {
    if (!acc[seat.row]) acc[seat.row] = [];
    acc[seat.row].push(seat);
    return acc;
  }, {});

  const rowNumbers = Object.keys(rowSeats)
    .map(Number)
    .sort((a, b) => a - b);

  return {
    flightId: data.flightId,
    flightNumber: data.flightNumber,
    aircraftCode: data.aircraftCode || data.flightNumber || "Aircraft",
    aircraftName: data.aircraftName,
    currencyCode,
    columns,
    sections: buildSections(rowNumbers, rowSeats, currencyCode),
    seats,
  };
};

const getNestedSeatHoldValue = (
  hold: SeatHoldResponse,
  objectKey: string,
  valueKey: string
) => {
  const nestedValue = hold[objectKey];
  if (!nestedValue || typeof nestedValue !== "object") return undefined;
  const value = (nestedValue as Record<string, unknown>)[valueKey];
  return typeof value === "string" ? value : undefined;
};

const getHoldIdentifiers = (hold: SeatHoldResponse) =>
  [
    hold.seatLabel,
    hold.seatId,
    hold.flightSeatId,
    hold.label,
    getNestedSeatHoldValue(hold, "seat", "id"),
    getNestedSeatHoldValue(hold, "seat", "label"),
    getNestedSeatHoldValue(hold, "seat", "seatId"),
    getNestedSeatHoldValue(hold, "seat", "seatLabel"),
    getNestedSeatHoldValue(hold, "flightSeat", "id"),
    getNestedSeatHoldValue(hold, "flightSeat", "label"),
  ]
    .filter((value): value is string => !!value)
    .map((value) => value.toUpperCase());

const getHoldOwnerKey = (hold: SeatHoldResponse) =>
  [
    hold.ownerKey,
    typeof hold.owner === "string" ? hold.owner : undefined,
    getNestedSeatHoldValue(hold, "owner", "key"),
    getNestedSeatHoldValue(hold, "owner", "ownerKey"),
  ].find((value): value is string => !!value);

const applySeatHolds = (
  layout: SeatMapLayout,
  holds: SeatHoldResponse[] = [],
  ownerKey?: string
): SeatMapLayout => {
  const heldIdentifiers = new Set<string>();

  holds.forEach((hold) => {
    if (hold.ownedByRequester) {
      return;
    }
    const holdOwnerKey = getHoldOwnerKey(hold);
    if (
      ownerKey &&
      holdOwnerKey &&
      holdOwnerKey.toUpperCase() === ownerKey.toUpperCase()
    ) {
      return;
    }
    getHoldIdentifiers(hold).forEach((identifier) =>
      heldIdentifiers.add(identifier)
    );
  });

  if (!heldIdentifiers.size) return layout;

  return {
    ...layout,
    seats: layout.seats.map((seat) => {
      const identifiers = [seat.label, seat.id, seat.flightSeatId]
        .filter((value): value is string => !!value)
        .map((value) => value.toUpperCase());
      const isHeld = identifiers.some((identifier) =>
        heldIdentifiers.has(identifier)
      );

      if (!isHeld) return seat;

      return {
        ...seat,
        status: "reserved",
        blocked: true,
        backendStatus: seat.backendStatus || "HELD",
      };
    }),
  };
};

const getErrorStatus = (error: any) => error?.status || error?.response?.status;

const shouldRetryAlternateSeatIdentifier = (error: any) => {
  const status = getErrorStatus(error);
  return status === 400 || status === 404 || status === 422;
};

const getSeatIdentifierCandidates = (request: SeatHoldRequest) =>
  Array.from(
    new Set([request.seatLabel, request.seatId].filter(Boolean) as string[])
  );

const normalizeRecommendedSeat = (
  seat: SeatMapApiSeat,
  fallbackCurrencyCode = "EUR"
): SeatMapSeat => {
  const rowNumber = Number(seat.rowNumber) || 0;
  const column = seat.columnLetter || "";
  const label = seat.label || `${rowNumber}${column}`;

  return {
    id: label,
    flightSeatId: seat.id || undefined,
    label,
    row: rowNumber,
    column,
    type: getSeatType(seat),
    seatClassName: normalizeSeatClassName(seat.seatClassName),
    status: getSeatStatus(seat),
    price: Number(seat.price) || 0,
    currencyCode: fallbackCurrencyCode,
    backendStatus: seat.status,
    nearAisle: !!seat.nearAisle,
    nearWindow: !!seat.nearWindow,
    nearExit: !!seat.nearExit,
    nearLavatory: !!seat.nearLavatory,
    overWing: !!seat.overWing,
    allowChildSelected: seat.allowChildSelected !== false,
    blocked: !!seat.blocked,
  };
};

export const assignRecommendedSeats = ({
  passengers,
  segments,
  layoutsByFlightId,
}: RecommendedSeatsRequest): SeatAssignment[] => {
  const seatablePassengers = passengers
    .map((passenger, passengerIndex) => ({ passenger, passengerIndex }))
    .filter(({ passenger }) => !isInfantPassenger(passenger));

  return segments.flatMap((segment) => {
    const layout = layoutsByFlightId[segment.flightId];
    if (!layout) return [];

    const availableSeats = layout.seats
      .filter((seat) => seat.status === "available")
      .sort((a, b) => {
        const seatTypeRank: Record<SeatCabinType, number> = {
          standard: 0,
          front: 1,
          "extra-legroom": 2,
        };
        return seatTypeRank[a.type] - seatTypeRank[b.type] || a.row - b.row;
      });

    return seatablePassengers
      .map(({ passenger, passengerIndex }, idx) => {
        const seat = availableSeats[idx];
        if (!seat) return null;

        return {
          segment: segment.id,
          flightId: segment.flightId,
          passengerIndex,
          passengerName: getPassengerName(passenger, passengerIndex),
          ageCategory: passenger.ageCategory,
          seatId: seat.id,
          flightSeatId: seat.flightSeatId,
          seatLabel: seat.label,
          seatType: seat.type,
          price: seat.price,
          currencyCode: seat.currencyCode,
        };
      })
      .filter(Boolean) as SeatAssignment[];
  });
};

class SeatMapService {
  public getSeatMap = async (body: SeatMapRequest): Promise<SeatMapLayout> => {
    try {
      const response = await axios.get<SeatMapApiResponse>(
        `/api/v1/flights/${encodeURIComponent(body.flightId)}/seat-map`
      );
      const layout = normalizeSeatMap(response.data, body.currencyCode);

      try {
        const holds = await this.getSeatHolds(body.flightId, body.ownerKey);
        return applySeatHolds(layout, holds, body.ownerKey);
      } catch (holdsError) {
        return layout;
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  public getRecommendedSeats = async (
    body: RecommendedSeatsRequest
  ): Promise<SeatAssignment[]> => {
    const seatablePassengers = body.passengers
      .map((passenger, passengerIndex) => ({ passenger, passengerIndex }))
      .filter(({ passenger }) => !isInfantPassenger(passenger));

    try {
      const assignments = await Promise.all(
        body.segments.map(async (segment) => {
          const limit =
            body.limit && body.limit > 0
              ? body.limit
              : Math.max(seatablePassengers.length, 1);
          const currencyCode =
            body.layoutsByFlightId?.[segment.flightId]?.currencyCode || "EUR";
          const response = await axios.get<SeatMapApiSeat[]>(
            `/api/v1/flights/${encodeURIComponent(
              segment.flightId
            )}/seats/recommended`,
            { params: { limit } }
          );
          const recommendedSeats = Array.isArray(response.data)
            ? response.data.map((seat) =>
                normalizeRecommendedSeat(seat, currencyCode)
              )
            : [];

          return seatablePassengers
            .map(({ passenger, passengerIndex }, idx) => {
              const seat = recommendedSeats[idx];
              if (!seat) return null;

              return {
                segment: segment.id,
                flightId: segment.flightId,
                passengerIndex,
                passengerName: getPassengerName(passenger, passengerIndex),
                ageCategory: passenger.ageCategory,
                seatId: seat.id,
                flightSeatId: seat.flightSeatId,
                seatLabel: seat.label,
                seatType: seat.type,
                price: seat.price,
                currencyCode: seat.currencyCode,
              } as SeatAssignment;
            })
            .filter(Boolean) as SeatAssignment[];
        })
      );

      return assignments.flat();
    } catch (error) {
      return assignRecommendedSeats(body);
    }
  };

  public holdSeat = async ({
    flightId,
    seatLabel,
    seatId,
    ownerKey,
    channel = "CUSTOMER_WEB",
  }: SeatHoldRequest): Promise<SeatHoldResponse> => {
    const identifiers = getSeatIdentifierCandidates({
      flightId,
      seatLabel,
      seatId,
      ownerKey,
      channel,
    });
    const primaryIdentifier = identifiers[0] || seatLabel;

    const postHold = async (identifier: string) => {
      const response = await axios.post<SeatHoldResponse>(
        `/api/v1/flights/${encodeURIComponent(
          flightId
        )}/seats/${encodeURIComponent(identifier)}/hold`,
        { ownerKey, channel }
      );

      return response.data;
    };

    try {
      return await postHold(primaryIdentifier);
    } catch (error) {
      if (identifiers[1] && shouldRetryAlternateSeatIdentifier(error)) {
        return postHold(identifiers[1]);
      }
      throw error;
    }
  };

  public releaseSeat = async ({
    flightId,
    seatLabel,
    seatId,
    ownerKey,
  }: SeatHoldRequest): Promise<void> => {
    const identifiers = getSeatIdentifierCandidates({
      flightId,
      seatLabel,
      seatId,
      ownerKey,
    });
    const primaryIdentifier = identifiers[0] || seatLabel;

    const deleteHold = async (identifier: string) => {
      await axios.delete(
        `/api/v1/flights/${encodeURIComponent(
          flightId
        )}/seats/${encodeURIComponent(identifier)}/hold`,
        { headers: { "X-Seat-Hold-Owner-Key": ownerKey } }
      );
    };

    try {
      await deleteHold(primaryIdentifier);
    } catch (error) {
      if (identifiers[1] && shouldRetryAlternateSeatIdentifier(error)) {
        await deleteHold(identifiers[1]);
        return;
      }
      throw error;
    }
  };

  public getSeatHolds = async (
    flightId: string,
    ownerKey?: string
  ): Promise<SeatHoldResponse[]> => {
    const response = await axios.get<SeatHoldResponse[]>(
      `/api/v1/flights/${encodeURIComponent(flightId)}/seats/holds`,
      {
        headers: ownerKey
          ? { "X-Seat-Hold-Owner-Key": ownerKey }
          : undefined,
      }
    );

    return response.data;
  };

  public releaseSeatHolds = async ({
    flightId,
    ownerKey,
  }: ReleaseSeatHoldsRequest): Promise<void> => {
    await axios.delete(
      `/api/v1/flights/${encodeURIComponent(flightId)}/seats/holds`,
      { headers: { "X-Seat-Hold-Owner-Key": ownerKey } }
    );
  };
}

export default new SeatMapService();
